import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import Select from '@/components/switchback/Select';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { AFFILIATE_AVANT_LINK, AFFILIATE_HAS_OFFERS } from '@/constants/urls';

import GridColumn from '../static-pages/wagtail/GridColumn/GridColumn';
import css from './AffiliateTemplate.module.css';

const CONTENT_GRID = 'content-grid';
const RAW = 'raw';
const HEADING = 'heading';
const IMAGE = 'image';

interface IRawContent {
  contentBlocks?: IBlock[];
  /* eslint-disable @typescript-eslint/no-explicit-any */
  rawRefs?: any;
}
interface HeroProps {
  heading: string;
  imageUrl: string;
}

interface IBlock {
  type: string;
  value: any;
}

const Hero: React.FC<HeroProps> = ({ heading, imageUrl }) => {
  return (
    <div className={css.hero}>
      <ResponsiveImage
        src={imageUrl}
        alt="background image"
        className="absolute object-cover w-full h-full"
        sourceSizes={[
          'landscape280',
          'landscape375',
          'landscape768',
          'landscape1080',
          'landscape1440',
          'landscape2160',
          'landscape2880',
        ]}
        sizes="100vw"
      />
      <h1 className={`z-10 mx-auto text-white h3 max-w-xs mt-10 md:mt-12 xl:mt-20 text-center`}>
        {heading}
      </h1>
    </div>
  );
};

interface IGetStartedProps {
  content: string;
}
const LeftCard: React.FC<IGetStartedProps> = ({ content }) => {
  return <div className={css.leftContent} dangerouslySetInnerHTML={{ __html: content }} />;
};

const RightCard: React.FC<IGetStartedProps> = ({ content }) => {
  return <div className={css.rightContent} dangerouslySetInnerHTML={{ __html: content }} />;
};

interface IContentGrid {
  description?: string;
  url: string;
}

const ContentGrid: React.FC<IRawContent> = ({ contentBlocks }) => {
  const contentGrid = contentBlocks?.find((block: IBlock) => block.type === CONTENT_GRID);
  if (contentGrid?.value && contentGrid.value.length) {
    const content = contentGrid?.value?.map((item: IContentGrid, idx: number) => {
      return (
        <GridColumn
          key={idx}
          className={css.content}
          description={item.description}
          imageProps={{
            height: 44,
            width: 44,
            src: item.url,
          }}
          headingLevel={5}
        />
      );
    });
    return (
      <section>
        <div className={`grid grid-cols-2 lg:grid-cols-3 mb-20 gap-16 max-w-74 box-content`}>
          <>{content}</>
        </div>
      </section>
    );
  }
  return null;
};

interface IPFooterProps {
  bottomHeading: string;
}
const Footer: React.FC<IPFooterProps> = ({ bottomHeading }) => {
  const intl = useIntl();
  const contactBtn = intl.formatMessage({
    defaultMessage: `Contact the Affiliate Team`,
    id: 'pToSuJ',
  });
  const readTermsBtn = intl.formatMessage({
    defaultMessage: `Read terms and conditions`,
    id: 'Z6Ie2/',
  });
  return (
    <div
      className={
        'flex items-center bg-gray-100 border-t border-gray-100 text-center justify-center px-4 sm:px-8 md:px-12 lg:px-16'
      }>
      <div className="my-10">
        <h4 className="my-10 text-gray-800 h4">{bottomHeading}</h4>
        <div className="mb-20 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <Button
              href="mailto:affiliates@outdoorsy.co"
              variant={EButtonColorVariant.Secondary}
              label={contactBtn}
            />
          </div>
          <div>
            <Button
              href="/affiliate-terms"
              variant={EButtonColorVariant.Primary}
              label={readTermsBtn}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface IProps {
  heroHeading?: string;
  heroImage?: string;
  contentBlocks?: IBlock[];
}

const Affiliate: React.FC<IProps> = ({ heroHeading = '', heroImage, contentBlocks }) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const intl = useIntl();
  const getStartedBtn = intl.formatMessage({
    defaultMessage: `Get Started`,
    id: '9QXm1E',
  });

  const options = [
    {
      label: intl.formatMessage({ defaultMessage: 'HasOffers', id: 'vsPxf3' }),
      value: AFFILIATE_HAS_OFFERS,
    },
    {
      label: intl.formatMessage({ defaultMessage: 'AvantLink', id: 'ERJmER' }),
      value: AFFILIATE_AVANT_LINK,
    },
  ];

  const images = contentBlocks?.filter((block: IBlock) => block.type === IMAGE) || [];
  const heroImageUrl = images[0]?.value.url || heroImage;

  const rawSections = contentBlocks?.filter((block: IBlock) => block.type === RAW) || [];
  const leftSection = rawSections[0]?.value;
  const rightContent = rawSections[1]?.value;

  const headings = contentBlocks?.filter((block: IBlock) => block.type === HEADING) || [];
  const topHeading = headings[0]?.value;
  const bottomHeading = headings[1]?.value;

  const handleOnClick = () => {
    if (selectRef.current?.value) {
      window.location.href = selectRef.current?.value;
    }
  };
  return (
    <main>
      {heroImage && <Hero heading={heroHeading} imageUrl={heroImageUrl} />}
      <div className={'mx-auto px-4 sm:px-8 md:px-12 lg:px-16 max-w-74'}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 ">
          <div className="mt-12 sm:mt-16 lg:mt-20 ">
            <LeftCard content={leftSection} />
          </div>
          <div
            className={`bg-white lg:relative xs:mt-12 mb-10 xs:mb-16 sm:mt-16 sm:mb-20 lg:m-0 rounded-box shadow-200 ${css.rightCard}`}>
            <div className="px-12 py-8">
              <RightCard content={rightContent} />
              <div className="pt-8">
                <Select ref={selectRef} options={options} />
              </div>
              <div className="pt-8">
                <Button
                  variant={EButtonColorVariant.Primary}
                  label={getStartedBtn}
                  fullWidth
                  onClick={handleOnClick}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={'mx-auto px-4 sm:px-8 md:px-12 lg:px-16 max-w-74'}>
        <h4 className="mb-10 text-gray-800 h4">{topHeading}</h4>
        <ContentGrid contentBlocks={contentBlocks} />
      </div>
      <Footer bottomHeading={bottomHeading} />
    </main>
  );
};

export default Affiliate;
